import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { toast } from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUtils';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    XMarkIcon,
    PhotoIcon,
} from '@heroicons/react/24/outline';

interface Slider {
    _id: string;
    title: string;
    image: string;
    link?: string;
    order: number;
    isActive: boolean;
}

const SlidersManagement: React.FC = () => {
    const [sliders, setSliders] = useState<Slider[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedSlider, setSelectedSlider] = useState<Slider | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        image: '',
        link: '',
        order: 0,
        isActive: true,
    });

    useEffect(() => {
        fetchSliders();
    }, []);

    const fetchSliders = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/sliders');
            setSliders(response.data.data || []);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to fetch sliders');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (slider?: Slider) => {
        if (slider) {
            setSelectedSlider(slider);
            setIsEditMode(true);
            setFormData({
                title: slider.title,
                image: slider.image,
                link: slider.link || '',
                order: slider.order,
                isActive: slider.isActive,
            });
        } else {
            setIsEditMode(false);
            setSelectedSlider(null);
            setFormData({
                title: '',
                image: '',
                link: '',
                order: 0,
                isActive: true,
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedSlider(null);
        setIsEditMode(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileUpload = async (file: File) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('folder', 'sliders');

            const response = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Use fullUrl if available, otherwise construct from url
            const API_URL = process.env.REACT_APP_API_URL || 'https://api.crm.we4task.com';
            const imageUrl = response.data.fullUrl || (response.data.url?.startsWith('http')
                ? response.data.url
                : `${API_URL}${response.data.url}`);

            setFormData(prev => ({ ...prev, image: imageUrl }));
            toast.success('Image uploaded successfully');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to upload image');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (isEditMode && selectedSlider) {
                await api.put(`/admin/sliders/${selectedSlider._id}`, formData);
                toast.success('Slider updated successfully');
            } else {
                await api.post('/admin/sliders', formData);
                toast.success('Slider created successfully');
            }
            handleCloseModal();
            fetchSliders();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to save slider');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this slider?')) return;

        try {
            await api.delete(`/admin/sliders/${id}`);
            toast.success('Slider deleted successfully');
            fetchSliders();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete slider');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="spinner"></div>
                    <p className="mt-4 text-gray-600">Loading sliders...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Slider Images Management</h1>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Add Slider
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sliders.map((slider) => (
                    <div key={slider._id} className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="relative h-48 bg-gray-200">
                            {slider.image ? (
                                <img
                                    src={getImageUrl(slider.image)}
                                    alt={slider.title}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <PhotoIcon className="w-12 h-12" />
                                </div>
                            )}
                            <div className="absolute top-2 right-2">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${slider.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                    {slider.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                        <div className="p-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{slider.title}</h3>
                            {slider.link && (
                                <p className="text-sm text-gray-600 mb-2 truncate">{slider.link}</p>
                            )}
                            <p className="text-sm text-gray-500 mb-4">Order: {slider.order}</p>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handleOpenModal(slider)}
                                    className="flex-1 px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                                >
                                    <PencilIcon className="w-4 h-4 inline mr-1" />
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(slider._id)}
                                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                                >
                                    <TrashIcon className="w-4 h-4 inline mr-1" />
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <form onSubmit={handleSubmit} className="bg-white">
                                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        {isEditMode ? 'Edit Slider' : 'Add New Slider'}
                                    </h3>
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="text-gray-400 hover:text-gray-500"
                                    >
                                        <XMarkIcon className="w-6 h-6" />
                                    </button>
                                </div>

                                <div className="px-6 py-4">
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Image *</label>
                                        <p className="text-xs text-gray-500 mb-2">📐 Recommended: 1920x600px (Full-width banner, 16:5 ratio)</p>
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                name="image"
                                                value={formData.image}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Image URL"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <label className="px-3 py-2 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200">
                                                <PhotoIcon className="w-5 h-5" />
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                                                />
                                            </label>
                                        </div>
                                        {formData.image && (
                                            <img src={getImageUrl(formData.image)} alt="Preview" className="mt-2 h-32 w-full object-cover rounded" />
                                        )}
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Link (Optional)</label>
                                        <input
                                            type="url"
                                            name="link"
                                            value={formData.link}
                                            onChange={handleInputChange}
                                            placeholder="https://example.com"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                                            <input
                                                type="number"
                                                name="order"
                                                value={formData.order}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="flex items-center pt-6">
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                checked={formData.isActive}
                                                onChange={handleInputChange}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label className="ml-2 block text-sm text-gray-700">Active</label>
                                        </div>
                                    </div>
                                </div>

                                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        {isEditMode ? 'Update' : 'Create'} Slider
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SlidersManagement;

